import { type ReactNode } from "react"
import { motion, useReducedMotion } from "motion/react"
import { Loader2, Mic, X, Check } from "lucide-react"
import { IconButton, Tooltip } from "@strivelabs/ui-core/v2"
import { ComposerWaveform } from "./waveform"

/** Format elapsed milliseconds as `m:ss`. */
const formatElapsed = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

export interface ComposerDictationBarProps {
  status: "recording" | "transcribing"
  elapsedMs: number
  levels: number[]
}

/**
 * The "Listening" strip that rises above the composer surface while dictating.
 * A narrow, centered pill: label, timer, waveform, and a "Stop to insert" hint.
 * Slides up on mount and collapses on exit; reduced motion fades only.
 */
export const ComposerDictationBar = ({
  status,
  elapsedMs,
  levels,
}: ComposerDictationBarProps) => {
  const reduceMotion = useReducedMotion()
  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
      animate={reduceMotion ? { opacity: 1 } : { opacity: 1, height: "auto" }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, height: 0 }}
      transition={{ type: "spring", duration: 0.3, bounce: 0 }}
      className="overflow-hidden"
    >
      <div className="mb-[-14px] mx-auto flex w-[92%] items-center gap-3 rounded-t-2xl border border-b-0 bg-muted/70 px-4 pb-5 pt-2.5">
        <span className="text-sm font-medium text-foreground">
          {status === "recording" ? "Listening" : "Transcribing…"}
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {formatElapsed(elapsedMs)}
        </span>
        <div className="flex flex-1 justify-center">
          <ComposerWaveform levels={levels} active={status === "recording"} />
        </div>
        <span className="text-xs text-muted-foreground">Stop to insert</span>
      </div>
    </motion.div>
  )
}

export interface ComposerDictateControlProps {
  status: "idle" | "recording" | "transcribing"
  onStart: () => void
  onStop: () => void
  onDiscard: () => void
  disabled?: boolean
  /** Device-picker element rendered next to the idle mic. */
  deviceMenu?: ReactNode
}

/**
 * Toolbar dictation control: a hover-revealed device caret plus mic when idle,
 * a discard + tick pair while recording (Claude-style), and a disabled spinner
 * while transcribing. Matches the Send button's geometry.
 */
export const ComposerDictateControl = ({
  status,
  onStart,
  onStop,
  onDiscard,
  disabled,
  deviceMenu,
}: ComposerDictateControlProps) => {
  if (status === "transcribing") {
    return (
      <button
        type="button"
        disabled
        aria-label="Transcribing"
        className="inline-flex size-9 items-center justify-center rounded-xl bg-muted text-muted-foreground"
      >
        <Loader2 className="size-4 animate-spin" />
      </button>
    )
  }

  if (status === "recording") {
    return (
      <div className="flex items-center gap-1">
        <Tooltip label="Discard">
          <button
            type="button"
            onClick={onDiscard}
            aria-label="Discard recording"
            className="inline-flex size-9 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <X className="size-[18px]" />
          </button>
        </Tooltip>
        <Tooltip label="Stop & insert">
          <button
            type="button"
            onClick={onStop}
            aria-label="Stop and insert"
            className="inline-flex size-9 items-center justify-center rounded-xl bg-[#1c1c1c] text-white transition-[background-color,transform] hover:bg-black active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Check className="size-[18px]" />
          </button>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="group flex items-center gap-0">
      {deviceMenu}
      <Tooltip label="Dictate" shortcut="⌘⇧D">
        <IconButton aria-label="Start dictation" onClick={onStart} disabled={disabled}>
          <Mic className="size-[17px]" />
        </IconButton>
      </Tooltip>
    </div>
  )
}
